import React from 'react';
import type { Question } from '../../types/game';

interface AdminStatsProps {
  stats: {
    totalQuestions: number;
    activeQuestions: number;
    totalCategories: number;
    activeCategories: number;
    mostUsedQuestions: Question[];
    topRatedQuestions: Question[];
    difficultyDistribution: {
      easy: number;
      medium: number;
      hard: number;
    };
    categoryStats: {
      [categoryId: string]: {
        name: string;
        totalQuestions: number;
        averageSuccessRate: number;
        totalCorrect: number;
        totalIncorrect: number;
      };
    };
  };
}

const AdminStats: React.FC<AdminStatsProps> = ({ stats }) => {
  return (
    <div>
      <h2 className="text-2xl font-bold text-brand-yellow mb-6">Game Statistics</h2>
      
      {/* Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-[#333] rounded-xl p-6">
          <h3 className="text-lg font-bold text-brand-yellow mb-2">Questions</h3>
          <div className="text-white">
            <p>Total: {stats.totalQuestions}</p>
            <p>Active: {stats.activeQuestions}</p>
          </div>
        </div>
        <div className="bg-[#333] rounded-xl p-6">
          <h3 className="text-lg font-bold text-brand-yellow mb-2">Categories</h3>
          <div className="text-white">
            <p>Total: {stats.totalCategories}</p>
            <p>Active: {stats.activeCategories}</p>
          </div>
        </div>
        <div className="bg-[#333] rounded-xl p-6">
          <h3 className="text-lg font-bold text-brand-yellow mb-2">Difficulty Distribution</h3>
          <div className="text-white">
            <p>Easy: {stats.difficultyDistribution.easy}</p>
            <p>Medium: {stats.difficultyDistribution.medium}</p>
            <p>Hard: {stats.difficultyDistribution.hard}</p>
          </div>
        </div>
        <div className="bg-[#333] rounded-xl p-6">
          <h3 className="text-lg font-bold text-brand-yellow mb-2">Overall Success</h3>
          <div className="text-white">
            <p>Total Correct: {Object.values(stats.categoryStats).reduce((sum, cat) => sum + cat.totalCorrect, 0)}</p>
            <p>Total Incorrect: {Object.values(stats.categoryStats).reduce((sum, cat) => sum + cat.totalIncorrect, 0)}</p>
          </div>
        </div>
      </div>

      {/* Category Performance */}
      <div className="mb-8">
        <h3 className="text-xl font-bold text-brand-yellow mb-4">Category Performance</h3>
        <div className="bg-[#333] rounded-xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-[#444] text-brand-yellow">
                <th className="py-3 px-4 text-left">Category</th>
                <th className="py-3 px-4 text-center">Questions</th>
                <th className="py-3 px-4 text-center">Success Rate</th>
                <th className="py-3 px-4 text-center">Correct</th>
                <th className="py-3 px-4 text-center">Incorrect</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(stats.categoryStats).map(([id, cat]) => (
                <tr key={id} className="border-b border-[#444] hover:bg-[#292929]">
                  <td className="py-3 px-4 text-white">{cat.name}</td>
                  <td className="py-3 px-4 text-center text-brand-yellow">{cat.totalQuestions}</td>
                  <td className="py-3 px-4 text-center text-brand-yellow">
                    {cat.averageSuccessRate.toFixed(1)}%
                  </td>
                  <td className="py-3 px-4 text-center text-green-400">{cat.totalCorrect}</td>
                  <td className="py-3 px-4 text-center text-red-400">{cat.totalIncorrect}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Most Used Questions */}
      <div className="mb-8">
        <h3 className="text-xl font-bold text-brand-yellow mb-4">Most Used Questions</h3>
        <div className="bg-[#333] rounded-xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-[#444] text-brand-yellow">
                <th className="py-3 px-4 text-left">Question</th>
                <th className="py-3 px-4 text-center">Usage</th>
                <th className="py-3 px-4 text-center">Success Rate</th>
                <th className="py-3 px-4 text-center">Correct</th>
                <th className="py-3 px-4 text-center">Incorrect</th>
                <th className="py-3 px-4 text-center">Avg. Time</th>
              </tr>
            </thead>
            <tbody>
              {stats.mostUsedQuestions.map(q => (
                <tr key={q.id} className="border-b border-[#444] hover:bg-[#292929]">
                  <td className="py-3 px-4 text-white">{q.text}</td>
                  <td className="py-3 px-4 text-center text-brand-yellow">{q.usageCount || 0}</td>
                  <td className="py-3 px-4 text-center text-brand-yellow">
                    {q.successRate ? q.successRate.toFixed(1) + '%' : '-'}
                  </td>
                  <td className="py-3 px-4 text-center text-green-400">{q.correctAnswers || 0}</td>
                  <td className="py-3 px-4 text-center text-red-400">{q.incorrectAnswers || 0}</td>
                  <td className="py-3 px-4 text-center text-brand-yellow">
                    {q.averageResponseTime ? q.averageResponseTime.toFixed(1) + 's' : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Top Rated Questions */}
      <div>
        <h3 className="text-xl font-bold text-brand-yellow mb-4">Top Rated Questions</h3>
        <div className="bg-[#333] rounded-xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-[#444] text-brand-yellow">
                <th className="py-3 px-4 text-left">Question</th>
                <th className="py-3 px-4 text-center">Rating</th>
                <th className="py-3 px-4 text-center">Votes</th>
                <th className="py-3 px-4 text-center">Success Rate</th>
                <th className="py-3 px-4 text-center">Usage</th>
              </tr>
            </thead>
            <tbody>
              {stats.topRatedQuestions.map(q => (
                <tr key={q.id} className="border-b border-[#444] hover:bg-[#292929]">
                  <td className="py-3 px-4 text-white">{q.text}</td>
                  <td className="py-3 px-4 text-center text-brand-yellow">
                    {q.rating ? q.rating.toFixed(1) : '-'}
                  </td>
                  <td className="py-3 px-4 text-center text-brand-yellow">{q.ratingCount || 0}</td>
                  <td className="py-3 px-4 text-center text-brand-yellow">
                    {q.successRate ? q.successRate.toFixed(1) + '%' : '-'}
                  </td>
                  <td className="py-3 px-4 text-center text-brand-yellow">{q.usageCount || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminStats; 